import React from 'react';
import { connect } from "react-redux";
import styles from "assets/jss/material-dashboard-pro-react/components/tableStyle";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Breadcrumbs,
  Link,
  CircularProgress,
  Paper,
  Tooltip,
  Card,
  CardContent,
  TablePagination,
  TextField,
  InputAdornment,
  Button
} from '@material-ui/core';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
  CloudDownload as DownloadIcon,
  Storage as BucketIcon,
  Sort as SortIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@material-ui/icons';
import { getS3BrowserCredentials, getS3Browser } from '../../api';
import { S3Client, ListObjectsV2Command, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { format } from 'date-fns';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  breadcrumbs: {
    marginBottom: theme.spacing(2)
  },
  list: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  },
  error: {
    color: theme.palette.error.main,
    padding: theme.spacing(2)
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: theme.spacing(1)
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  cardContent: {
    flex: 1,
    padding: 0,
    '&:last-child': {
      paddingBottom: 0
    }
  },
  pagination: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  searchBar: {
    marginLeft: theme.spacing(2),
    width: 300
  }
}));

class StorageBrowser extends React.Component {
  state = {
    buckets: [],
    selectedBucket: null,
    currentPath: '',
    items: [],
    loading: false,
    error: null,
    credentials: null,
    s3Client: null,
    sortDirection: 'desc', // 'asc' or 'desc'
    page: 0,
    rowsPerPage: 100,
    searchPrefix: '',
    continuationToken: null,
    isTruncated: false
  };

  componentDidMount() {
    this.initializeS3();
  }

  async initializeS3() {
    try {
      const response = await getS3BrowserCredentials();
      const credentials = response.data.credentials;
      
      
      // Ensure credentials are in the correct format for AWS SDK v3
      const awsCredentials = {
        accessKeyId: credentials.AccessKeyId || credentials.accessKeyId,
        secretAccessKey: credentials.SecretAccessKey || credentials.secretAccessKey,
        sessionToken: credentials.SessionToken || credentials.sessionToken
      };

      const s3Client = new S3Client({
        credentials: awsCredentials,
        region: 'us-west-2'
      });
      this.setState({ credentials: awsCredentials, s3Client }, () => {
        this.loadBuckets();
      });
    } catch (error) {
      console.error('S3 initialization error:', error);
      this.setState({ error: 'Failed to initialize S3 client' });
    }
  }

  async loadBuckets() {
    this.setState({ loading: true, error: null });
    try {
      const response = await getS3Browser();
      this.setState({ buckets: response.data.items });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  }

  async loadItems() {
    const { s3Client, selectedBucket, currentPath, searchPrefix, continuationToken } = this.state;
    if (!s3Client || !selectedBucket) return;

    this.setState({ loading: true, error: null });
    try {
      const command = new ListObjectsV2Command({
        Bucket: selectedBucket.bucket,
        Prefix: currentPath + searchPrefix,
        Delimiter: '/',
        MaxKeys: 1000,
        ContinuationToken: continuationToken
      });

      const data = await s3Client.send(command);
      
      const newItems = [];
      
      // Add folders (CommonPrefixes)
      if (data.CommonPrefixes) {
        newItems.push(...data.CommonPrefixes.map(prefix => ({
          type: 'PREFIX',
          name: prefix.Prefix.slice(currentPath.length).split('/')[0],
          prefix: prefix.Prefix,
          permissions: selectedBucket.permissions,
          lastModified: new Date(0) // Folders don't have last modified date, use epoch
        })));
      }

      // Add files (Contents)
      if (data.Contents) {
        newItems.push(...data.Contents
          .filter(item => item.Key !== currentPath) // Skip the current directory
          .map(item => ({
            type: 'OBJECT',
            name: item.Key.slice(currentPath.length),
            key: item.Key,
            size: this.formatFileSize(item.Size),
            bucket: selectedBucket.bucket,
            permissions: selectedBucket.permissions,
            lastModified: item.LastModified
          })));
      }

      // Combine existing items with new items and sort the entire list
      const combinedItems = continuationToken ? [...this.state.items, ...newItems] : newItems;
      combinedItems.sort((a, b) => {
        const dateA = a.lastModified.getTime();
        const dateB = b.lastModified.getTime();
        return this.state.sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
      });

      this.setState({
        items: combinedItems,
        continuationToken: data.NextContinuationToken,
        isTruncated: data.IsTruncated,
        page: 0 // Reset to first page when new items are loaded
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  handleBucketSelect = (bucket) => {
    this.setState({
      selectedBucket: bucket,
      currentPath: '',
      items: []
    }, () => this.loadItems());
  };

  handleItemClick = (item) => {
    if (item.type === 'PREFIX') {
      this.setState(
        { 
          currentPath: item.prefix,
          searchPrefix: '' // Clear search when entering a folder
        },
        () => this.loadItems()
      );
    }
  };

  handleBreadcrumbClick = (index) => {
    const pathParts = this.state.currentPath.split('/').filter(Boolean);
    const newPath = pathParts.slice(0, index + 1).join('/') + '/';
    this.setState(
      { currentPath: newPath },
      () => this.loadItems()
    );
  };

  handleRefresh = () => {
    if (this.state.selectedBucket) {
      this.loadItems();
    } else {
      this.loadBuckets();
    }
  };

  handleDownload = async (item) => {
    const { s3Client } = this.state;
    if (!s3Client) return;

    try {
      const command = new GetObjectCommand({
        Bucket: item.bucket,
        Key: item.key
      });

      const url = await getSignedUrl(s3Client, command, { expiresIn: 3600 });
      window.open(url, '_blank');
    } catch (error) {
      this.setState({ error: 'Failed to download file' });
    }
  };

  handleSortToggle = () => {
    this.setState(prevState => ({
      sortDirection: prevState.sortDirection === 'desc' ? 'asc' : 'desc'
    }), () => {
      if (this.state.selectedBucket) {
        this.loadItems();
      }
    });
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    });
  };

  handleSearchChange = (event) => {
    const searchPrefix = event.target.value;
    this.setState({ searchPrefix }, () => {
      // Debounce the search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.loadItems();
      }, 300);
    });
  };

  handleClearSearch = () => {
    this.setState({ searchPrefix: '' }, () => {
      this.loadItems();
    });
  };

  handleLoadMore = () => {
    if (this.state.isTruncated && this.state.continuationToken) {
      this.loadItems();
    }
  };

  render() {
    const { 
      buckets, 
      selectedBucket, 
      currentPath, 
      items, 
      loading, 
      error, 
      sortDirection,
      page,
      rowsPerPage,
      searchPrefix,
      isTruncated
    } = this.state;
    const { classes } = this.props;

    const pathParts = currentPath.split('/').filter(Boolean);

    // Calculate pagination
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const displayedItems = items.slice(startIndex, endIndex);

    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className={classes.root}>
            <Paper elevation={0}>
              <div className={classes.header}>
                <Breadcrumbs className={classes.breadcrumbs} separator={<NavigateNextIcon fontSize="small" />}>
                  <Link
                    color="inherit"
                    onClick={() => this.setState({ selectedBucket: null, currentPath: '', searchPrefix: '' }, () => this.loadBuckets())}
                    style={{ cursor: 'pointer' }}
                  >
                    Buckets
                  </Link>
                  {selectedBucket && (
                    <Link
                      color="inherit"
                      onClick={() => this.setState({ currentPath: '', searchPrefix: '' }, () => this.loadItems())}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedBucket.bucket}
                    </Link>
                  )}
                  {selectedBucket && pathParts.map((part, index) => (
                    <Link
                      key={index}
                      color="inherit"
                      onClick={() => this.handleBreadcrumbClick(index)}
                      style={{ cursor: 'pointer' }}
                    >
                      {part}
                    </Link>
                  ))}
                </Breadcrumbs>
                {selectedBucket && (
                  <TextField
                    className={classes.searchBar}
                    placeholder="Search files..."
                    value={searchPrefix}
                    onChange={this.handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchPrefix && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={this.handleClearSearch}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
                <div className={classes.headerActions}>
                  {selectedBucket && (
                    <Tooltip title={`Sort by date (${sortDirection === 'desc' ? 'newest first' : 'oldest first'})`}>
                      <IconButton onClick={this.handleSortToggle}>
                        <SortIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton onClick={this.handleRefresh}>
                    <RefreshIcon />
                  </IconButton>
                </div>
              </div>

              {error && (
                <Typography className={classes.error}>
                  {error}
                </Typography>
              )}

              {loading ? (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  <List className={classes.list}>
                    {!selectedBucket ? (
                      // Show buckets
                      buckets.map((bucket, index) => (
                        <ListItem
                          key={index}
                          button
                          onClick={() => this.handleBucketSelect(bucket)}
                        >
                          <ListItemIcon>
                            <BucketIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={bucket.bucket}
                            secondary={`ID: ${bucket.id}`}
                          />
                        </ListItem>
                      ))
                    ) : (
                      // Show items in selected bucket
                      displayedItems.map((item, index) => (
                        <ListItem
                          key={index}
                          button={item.type === 'PREFIX'}
                          onClick={() => this.handleItemClick(item)}
                        >
                          <ListItemIcon>
                            {item.type === 'PREFIX' ? <FolderIcon /> : <FileIcon />}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.name}
                            secondary={
                              <React.Fragment>
                                {item.type === 'OBJECT' && (
                                  <>
                                    {item.size}
                                    {' • '}
                                  </>
                                )}
                                {format(item.lastModified, 'MMM d, yyyy h:mm a')}
                              </React.Fragment>
                            }
                          />
                          {item.type === 'OBJECT' && item.permissions.includes('get') && (
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => this.handleDownload(item)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      ))
                    )}
                  </List>
                  {selectedBucket && items.length > 0 && (
                    <>
                      <TablePagination
                        component="div"
                        count={items.length}
                        page={page}
                        onChangePage={this.handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onChangeRowsPerPage={this.handleChangeRowsPerPage}
                        rowsPerPageOptions={[50, 100, 250, 500]}
                        className={classes.pagination}
                      />
                      {isTruncated && (
                        <div style={{ textAlign: 'center', padding: '16px' }}>
                          <Button
                            variant="outlined"
                            onClick={this.handleLoadMore}
                            disabled={loading}
                          >
                            Load More
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </Paper>
          </div>
        </CardContent>
      </Card>
    );
  }
}

export default connect((state) => ({ user: state.app.user }))(
  withStyles(styles)(StorageBrowser)
);